/**
 * The Scores search, ISSUE-1380 (v1: score_search/ui/score_search_screen.dart).
 * One search field, then the server's Teams and Players sections. A team
 * row opens the team page and a player row the player page, with the same
 * params a roster row sends.
 *
 * The field is local while a fan types; once typing pauses for
 * SCORE_SEARCH_DEBOUNCE_MS the text moves into `?q=` (replace, so Back does
 * not walk through every keystroke), and the read follows the URL. The
 * matching and the order are sports-api's (POST /search/teams-players); the
 * previous answer stays on screen while the next one loads, so the list does
 * not flash on every letter.
 */
import { useSearchTeamsPlayers } from "@sportsmet/sports-sdk/private";
import { FOCUS_RING, Input, Skeleton } from "@sportsmet/ui";
import { keepPreviousData } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";

import { extractErrorCode, showApiError } from "../../errors/apiErrorBus";
import { ChevronRightIcon } from "../../icons/glyphs";
import { TeamMark } from "../gameDetail/TeamMark";
import { PlayerHeadshot } from "../playerProfile/PlayerHeadshot";
import { DetailNavBar } from "../scores/DetailNavBar";
import { TeamReadFailed } from "../team/TeamReadFailed";
import {
  playerResultMeta,
  playerResultSearch,
  SCORE_SEARCH_BACK_LABEL,
  SCORE_SEARCH_DEBOUNCE_MS,
  SCORE_SEARCH_FAILED_LINE,
  SCORE_SEARCH_HINT,
  SCORE_SEARCH_INPUT_LABEL,
  SCORE_SEARCH_LOADING_LABEL,
  SCORE_SEARCH_PLACEHOLDER,
  SCORE_SEARCH_PLAYERS_HEADING,
  SCORE_SEARCH_TEAMS_HEADING,
  SCORE_SEARCH_TITLE,
  scoreSearchEmptyLine,
  searchableQuery,
  teamResultMeta,
} from "./scoreSearchLogic";

const ROW_CLASS = `flex min-h-14 items-center gap-3 border-t border-hairline px-4 py-2 text-ink no-underline transition-colors duration-200 hover:bg-surface-sunken motion-reduce:transition-none ${FOCUS_RING}`;

const ResultSection = (props: {
  heading: string;
  testId: string;
  children: ReactNode;
}): ReactNode => (
  <section
    data-testid={props.testId}
    className="flex flex-col overflow-hidden rounded-md border border-hairline bg-surface pt-3"
  >
    <h2 className="px-4 pb-2 text-body-strong text-ink">{props.heading}</h2>
    <ul className="flex flex-col">{props.children}</ul>
  </section>
);

const RowText = (props: { title: string; meta: string }): ReactNode => (
  <span className="flex min-w-0 flex-1 flex-col">
    <span className="truncate text-body-strong text-ink">{props.title}</span>
    <span className="truncate text-small text-ink-secondary">{props.meta}</span>
  </span>
);

export const ScoreSearchScreen = (props: {
  /** `?q=`, the text the read is asked for. */
  q: string | undefined;
  /** Moves the typed text into the URL, replacing the current entry. */
  onQueryChange: (next: string | undefined) => void;
}): ReactNode => {
  const { q, onQueryChange } = props;
  const [draft, setDraft] = useState(q ?? "");

  useEffect(() => {
    const next = draft.trim() === "" ? undefined : draft;
    if (next === q) return;
    const timer = setTimeout(() => {
      onQueryChange(next);
    }, SCORE_SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [draft, q, onQueryChange]);

  const query = searchableQuery(q);
  const read = useSearchTeamsPlayers(
    { query: query ?? "" },
    { enabled: query !== null, placeholderData: keepPreviousData },
  );

  useEffect(() => {
    if (extractErrorCode(read.data) !== null) {
      showApiError(read.data, undefined, read.dataUpdatedAt);
    }
  }, [read.dataUpdatedAt]);

  let body: ReactNode;
  if (query === null) {
    body = (
      <p
        data-testid="score-search-hint"
        className="text-body text-ink-secondary"
      >
        {SCORE_SEARCH_HINT}
      </p>
    );
  } else if (
    extractErrorCode(read.data) !== null ||
    (read.isError && read.data === undefined)
  ) {
    body = (
      <TeamReadFailed
        layout="panel"
        testId="score-search-failed"
        line={SCORE_SEARCH_FAILED_LINE}
        onRetry={() => {
          void read.refetch();
        }}
      />
    );
  } else if (read.data?.data == null) {
    body = (
      <div data-testid="score-search-loading">
        <Skeleton shape="text" lines={4} label={SCORE_SEARCH_LOADING_LABEL} />
      </div>
    );
  } else {
    const { teams, players } = read.data.data;
    body =
      teams.length === 0 && players.length === 0 ? (
        <p
          data-testid="score-search-empty"
          className="text-body text-ink-secondary"
        >
          {scoreSearchEmptyLine(read.data.data.query)}
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {teams.length > 0 ? (
            <ResultSection
              heading={SCORE_SEARCH_TEAMS_HEADING}
              testId="score-search-teams"
            >
              {teams.map((team) => (
                <li key={team.teamId}>
                  <Link
                    to="/scores"
                    search={{ teamId: team.teamId }}
                    data-testid="score-search-team-row"
                    className={ROW_CLASS}
                  >
                    <TeamMark
                      name={team.name}
                      abbreviation={team.abbreviation}
                      logoUrl={team.logoUrl ?? null}
                      size="md"
                    />
                    <RowText title={team.name} meta={teamResultMeta(team)} />
                    <ChevronRightIcon className="text-ink-tertiary" />
                  </Link>
                </li>
              ))}
            </ResultSection>
          ) : null}
          {players.length > 0 ? (
            <ResultSection
              heading={SCORE_SEARCH_PLAYERS_HEADING}
              testId="score-search-players"
            >
              {players.map((player) => (
                <li key={player.playerId}>
                  <Link
                    to="/scores/player"
                    search={playerResultSearch(player)}
                    data-testid="score-search-player-row"
                    className={ROW_CLASS}
                  >
                    <PlayerHeadshot
                      headshotUrl={player.headshotUrl ?? null}
                      size="md"
                    />
                    <RowText
                      title={player.name}
                      meta={playerResultMeta(player)}
                    />
                    <ChevronRightIcon className="text-ink-tertiary" />
                  </Link>
                </li>
              ))}
            </ResultSection>
          ) : null}
        </div>
      );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="score-search">
      <DetailNavBar
        backLabel={SCORE_SEARCH_BACK_LABEL}
        clearKeys={["q"]}
        title={SCORE_SEARCH_TITLE}
        testId="score-search-nav"
      />
      <Input
        label={SCORE_SEARCH_INPUT_LABEL}
        labelHidden
        type="search"
        inputMode="search"
        enterKeyHint="search"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        autoFocus
        placeholder={SCORE_SEARCH_PLACEHOLDER}
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        data-testid="score-search-input"
      />
      {body}
    </div>
  );
};
